
import { verifyAliyunCaptcha } from "@/utils/aliyunCaptcha";

const EMAIL_REG = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
const RESEND_COOLDOWN_SEC = 60;
const CHECK_EMAIL_DEBOUNCE_MS = 500;

export default {
  name: "AuthFreeLoginModal",
  data() {
    return {
      dialogVisible: false,
      verifyDialogVisible: false,
      submitLoading: false,
      checkEmailLoading: false,
      resendLoading: false,
      isRegistered: true,
      sentEmail: "",
      lastIsAgree: 1,
      resendCountdown: 0,
      resendTimer: null,
      checkEmailDebounceTimer: null,
      /** 递增以忽略过期的 checkEmail 响应 */
      checkEmailSeq: 0,
      form: {
        email: "",
        agree: true,
      },
    };
  },
  computed: {
    rules() {
      return {
        email: [
          { required: true, message: this.$t('page.ENTER_YOUR_EMAIL'), trigger: "blur" },
          { pattern: EMAIL_REG, message: this.$t('page.ENTER_VALID_EMAIL'), trigger: "blur" },
        ],
      };
    },
  },
  methods: {
    openDialog(email = "") {
      this.clearCheckEmailDebounce();
      this.checkEmailSeq += 1;
      this.verifyDialogVisible = false;
      this.clearResendTimer();
      this.sentEmail = "";
      this.checkEmailLoading = false;
      this.dialogVisible = true;
      this.isRegistered = true;
      this.form.agree = true;
      this.form.email = email || "";
      this.$nextTick(() => {
        if (this.$refs.freeForm) {
          this.$refs.freeForm.clearValidate();
        }
        if (this.form.email && EMAIL_REG.test(this.form.email)) {
          this.onEmailInput();
        }
      });
    },
    closeDialog() {
      this.clearCheckEmailDebounce();
      this.dialogVisible = false;
      this.$nextTick(() => {
        this.$refs.freeForm && this.$refs.freeForm.resetFields();
      });
    },
    clearCheckEmailDebounce() {
      if (this.checkEmailDebounceTimer) {
        clearTimeout(this.checkEmailDebounceTimer);
        this.checkEmailDebounceTimer = null;
      }
    },
    onEmailInput() {
      this.clearCheckEmailDebounce();
      const email = this.form.email;
      if (!email || !EMAIL_REG.test(email)) {
        this.checkEmailLoading = false;
        this.isRegistered = true;
        this.checkEmailSeq += 1;
        return;
      }
      this.checkEmailDebounceTimer = setTimeout(() => {
        this.checkEmailDebounceTimer = null;
        const seq = ++this.checkEmailSeq;
        this.runCheckEmail(email, seq);
      }, CHECK_EMAIL_DEBOUNCE_MS);
    },
    async runCheckEmail(email, seq) {
      this.checkEmailLoading = true;
      try {
        const res = await this.$global.checkEmail({ email });
        if (seq !== this.checkEmailSeq) return;
        if (this.form.email !== email) return;
        const data = res?.data;
        const isRegistered = data?.exist === true;
        this.isRegistered = !!isRegistered;
      } catch (error) {
        console.error("checkEmail error:", error);
      } finally {
        if (seq === this.checkEmailSeq) {
          this.checkEmailLoading = false;
        }
      }
    },
    closeVerifyDialog() {
      this.verifyDialogVisible = false;
    },
    onVerifyDialogClosed() {
      this.clearResendTimer();
      this.resendCountdown = 0;
    },
    startResendCountdown() {
      this.clearResendTimer();
      this.resendCountdown = RESEND_COOLDOWN_SEC;
      this.resendTimer = setInterval(() => {
        this.resendCountdown -= 1;
        if (this.resendCountdown <= 0) {
          this.clearResendTimer();
          this.resendCountdown = 0;
        }
      }, 1000);
    },
    clearResendTimer() {
      if (this.resendTimer) {
        clearInterval(this.resendTimer);
        this.resendTimer = null;
      }
    },
    openVerifySuccessDialog() {
      this.sentEmail = this.form.email;
      this.lastIsAgree = this.form.agree ? 1 : 0;
      this.dialogVisible = false;
      this.verifyDialogVisible = true;
      this.$nextTick(() => {
        this.startResendCountdown();
      });
    },
    async sendPasswordFreeWithCaptcha(email, isAgree) {
      const sceneRes = await this.$global.getSceneId();
      const sceneId = sceneRes?.data?.encrypted_scene_id || sceneRes?.data || "";
      if (!sceneId) {
        this.$message.error(this.$t('page.GET_VERIFICATION_SCENE_FAILED'));
        return { ok: false };
      }
      const captchaResult = await verifyAliyunCaptcha({
        EncryptedSceneId: sceneId,
        language: "en",
      });
      if (!captchaResult) {
        this.$message.error(this.$t('page.VERIFICATION_FAILED'));
        return { ok: false };
      }
      const params = {
        email,
        captcha: captchaResult,
      }
      if (!this.isRegistered) {
        params.is_agree = this.form.agree ? 1 : 0;
      }
      const res = await this.$global.sendPasswordFreeEmail(params, {
        jumpUrl: window.location.origin + '/user/gallery?type=freeLogin',
        lang: this.$i18n.locale || "en",
      });
      const success = res?.code === 1;
      if (!success) {
        this.$message.error(res?.msg || this.$t('page.SEND_FAILED'));
      }
      return { ok: success, res };
    },
    async handleResendEmail() {
      if (!this.sentEmail) return;
      this.resendLoading = true;
      try {
        const { ok } = await this.sendPasswordFreeWithCaptcha(
          this.sentEmail,
          this.lastIsAgree
        );
        if (ok) {
          this.$message.success(this.$t('page.EMAIL_SENT'));
          this.startResendCountdown();
        }
      } catch (error) {
        console.error("resend password-free email error:", error);
        this.$message.error(error?.message || this.$t('page.SYSTEM_ERROR'));
      } finally {
        this.resendLoading = false;
      }
    },
    async handleSubmit() {
      if (this.checkEmailLoading) {
        this.$message.warning(this.$t('page.CHECKING_EMAIL'));
        return;
      }
      this.$refs.freeForm.validate(async (valid) => {
        if (!valid) return;
        if (this.checkEmailLoading) {
          return;
        }
        if (!this.isRegistered && !this.form.agree) {
          this.$message.warning(this.$t('page.AGREE_TERMS_FIRST'));
          return;
        }
        this.submitLoading = true;
        try {
          const { ok } = await this.sendPasswordFreeWithCaptcha(
            this.form.email,
            this.form.agree ? 1 : 0
          );
          if (ok) {
            this.openVerifySuccessDialog();
          }
        } catch (error) {

          if (error?.message === "Captcha closed by user") return;

          this.$message.error(error?.message || this.$t('page.SYSTEM_ERROR'));
        } finally {
          this.submitLoading = false;
        }
      });
    },
    openPasswordLogin() {
      const email = this.form.email;
      this.closeDialog();
      this.$emit("open-password-login", email);
    },
  },
  beforeDestroy() {
    this.clearCheckEmailDebounce();
    this.clearResendTimer();
  },
};
